import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'bottom',
  delay = 500,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    const id = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const tooltipX = rect.left + rect.width / 2;
        const tooltipY = position === 'top' ? rect.top - 8 : rect.bottom + 8;
        
        setTooltipPosition({ x: tooltipX, y: tooltipY });
        setIsVisible(true);
      }
    }, delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  const getTooltipClasses = () => {
    const baseClasses = 'fixed bg-gray-900 text-white text-xs font-medium px-2 py-1 rounded whitespace-nowrap transform -translate-x-1/2';
    const positionClasses = position === 'top' ? '-translate-y-full' : '';
    return `${baseClasses} ${positionClasses}`;
  };

  const getArrowClasses = () => {
    const baseClasses = 'absolute w-0 h-0';
    switch (position) {
      case 'top':
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2 border-t-gray-900 border-t-4 border-l-transparent border-r-transparent border-l-4 border-r-4`;
      case 'bottom':
      default:
        return `${baseClasses} bottom-full left-1/2 transform -translate-x-1/2 border-b-gray-900 border-b-4 border-l-transparent border-r-transparent border-l-4 border-r-4`;
    }
  };

  const tooltipElement = isVisible ? (
    <div
      className={getTooltipClasses()}
      style={{ 
        left: tooltipPosition.x, 
        top: tooltipPosition.y,
        zIndex: 9999
      }}
    >
      {content}
      <div className={getArrowClasses()}></div>
    </div>
  ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  );
}; 